import { getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { STORY_BEATS_MAX, STORY_LIMITS, describeWritingTime, parseSortKey, wordCount, type SceneChanges, type StoryProject, type WritingSession } from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';
import type { SceneView } from '../db/protocol.ts';
import { docFromHtml, editProblems, htmlFromDoc } from '../editor/html.ts';
import { RichTextEditor } from '../editor/RichTextEditor.tsx';
import { SaveStatusLine, useAutosaver } from '../editor/useEntrySaver.tsx';
import { settings } from '../settings.ts';
import { keyLabel, keysOf, useShortcut, useShortcutKeys } from '../shortcuts.ts';
import { DateFields, Picker, draftOf, type DateDraft } from './ChronologyFields.tsx';
import { EntryContent } from './EntryContent.tsx';
import { FocusMode, type Sitting } from './FocusMode.tsx';
import { SceneScan } from './SceneScan.tsx';

/**
 * Writing a scene (docs/PARITY-STEP-5-PLAN.md section 4), as the desktop's scene editor:
 * its title and manuscript in the entry editor, story beats, its date in the Chronology,
 * the entries and events it involves, word goals with their bars, and its revisions to
 * look at and restore. Every change is autosaved, as an entry's is. Focus mode writes the
 * same text full screen, and logs the sitting.
 */

const realmRoute = getRouteApi('/_app/realm/$realmKey');

const number = (n: number) => n.toLocaleString('en');
const message = (e: unknown, fallback: string) => (e instanceof RealmClientError ? e.message : fallback);

/**
 * This tab's writing session, as the desktop counts it since the app started: each scene's
 * words when first opened, what they are now, and whose project it is.
 */
const session = new Map<string, { projectId: string; start: number; now: number }>();
function sessionWords(projectId: string): number {
  let total = 0;
  for (const s of session.values()) if (s.projectId === projectId) total += s.now - s.start;
  return Math.max(0, total);
}

export interface SceneNavigation {
  /** "Scene 3 of 6". */
  readonly at: number;
  readonly total: number;
  readonly previous: string | null;
  readonly next: string | null;
  readonly open: (sceneId: string) => Promise<void>;
}

export function SceneEditor({
  file,
  realmKey,
  project,
  view,
  navigation,
  tools,
  below,
}: {
  file: string;
  realmKey: string;
  project: StoryProject;
  view: SceneView;
  navigation: SceneNavigation;
  /** The page's own buttons, after the editor's. */
  tools: ReactNode;
  /** Under the buttons: a confirmation, when there is one. */
  below: ReactNode;
}) {
  const { scene } = view;
  const router = useRouter();
  const navigate = useNavigate();
  const { entries } = realmRoute.useLoaderData();
  useShortcutKeys();
  const { change, flush, status } = useAutosaver<SceneChanges>((changes) => realms.updateScene(file, scene.id, changes, settings()['writing.sceneRevisions']));
  const [title, setTitle] = useState(scene.title);
  const [goal, setGoal] = useState(scene.wordGoal > 0 ? String(scene.wordGoal) : '');
  const [beats, setBeats] = useState(scene.storyBeats);
  const [hasDate, setHasDate] = useState(scene.timelinePosition !== null);
  const [date, setDate] = useState<DateDraft>(draftOf(scene.timelinePosition));
  const [dateProblem, setDateProblem] = useState('');
  const [entryIds, setEntryIds] = useState(scene.entryIds);
  const [eventIds, setEventIds] = useState(scene.eventIds);
  const [focus, setFocus] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  /** The text as last edited; a new editor (a new generation) after focus mode or a restore. */
  const html = useRef(scene.contentHtml);
  const [generation, setGeneration] = useState(0);
  const [words, setWords] = useState(scene.wordCount);
  const problems = useMemo(() => editProblems(scene.contentHtml), [scene.contentHtml]);

  if (!session.has(scene.id)) session.set(scene.id, { projectId: project.id, start: scene.wordCount, now: scene.wordCount });
  const counted = (next: number) => {
    setWords(next);
    session.set(scene.id, { ...session.get(scene.id)!, now: next });
  };

  // A saved title or goal changes the outline beside the scene: reload it once saved.
  const outlineChanged = useRef(false);
  useEffect(() => {
    if (status.state === 'saved' && outlineChanged.current) {
      outlineChanged.current = false;
      void router.invalidate();
    }
  }, [status, router]);
  const update = (changes: SceneChanges, affectsOutline = false) => {
    if (affectsOutline) outlineChanged.current = true;
    change(changes);
  };

  function changeDate(on: boolean, draft: DateDraft) {
    setHasDate(on);
    setDate(draft);
    if (!on) {
      setDateProblem('');
      return update({ timelinePosition: null });
    }
    try {
      update({ timelinePosition: { sortKey: parseSortKey(draft.key), label: draft.label, uncertain: draft.uncertain } });
      setDateProblem('');
    } catch (e) {
      setDateProblem((e as Error).message);
    }
  }

  const openFocus = () => {
    if (problems.length === 0) setFocus(true);
  };
  useShortcut('writing.save', () => void flush());
  useShortcut('story.focus', openFocus, !focus && !scanning);
  useShortcut('story.previousScene', () => navigation.previous && void navigation.open(navigation.previous), !focus && navigation.previous !== null);
  useShortcut('story.nextScene', () => navigation.next && void navigation.open(navigation.next), !focus && navigation.next !== null);

  async function finishSitting(sitting: Sitting) {
    setFocus(false);
    html.current = sitting.html;
    counted(wordCount(sitting.html));
    setGeneration((g) => g + 1);
    try {
      await flush();
      await realms.recordWritingSession(file, { projectId: project.id, sceneId: scene.id, sceneTitle: title, ...sitting });
      await router.invalidate();
    } catch (e) {
      setError(message(e, 'The sitting could not be added to the writing log.'));
    }
  }

  const keys = (id: string) => keysOf(id).map(keyLabel)[0];
  const goalNumber = Number(goal) || 0;
  const sessionNow = sessionWords(project.id);

  return (
    <section aria-labelledby="story-heading" className="scene-editor">
      <p className="eyebrow">Scene</p>
      <label className="scene-title">
        <span className="visually-hidden">Scene title</span>
        <input
          id="story-heading"
          value={title}
          required
          maxLength={STORY_LIMITS.name}
          onChange={(e) => {
            setTitle(e.currentTarget.value);
            update({ title: e.currentTarget.value }, true);
          }}
        />
      </label>
      <div className="scene-status">
        <SaveStatusLine status={status} />
        <span className="meta">
          Scene {navigation.at} of {navigation.total} · {number(words)} {words === 1 ? 'word' : 'words'}
        </span>
      </div>
      <div className="actions">
        <button type="button" disabled={!navigation.previous} title={`The scene before this one${keys('story.previousScene') ? ` (${keys('story.previousScene')})` : ''}`} onClick={() => navigation.previous && void navigation.open(navigation.previous)}>
          ◀ Previous
        </button>
        <button type="button" disabled={!navigation.next} title={`The scene after this one${keys('story.nextScene') ? ` (${keys('story.nextScene')})` : ''}`} onClick={() => navigation.next && void navigation.open(navigation.next)}>
          Next ▶
        </button>
        <button type="button" disabled={problems.length > 0 || scanning} title={`Write full screen with nothing else in view${keys('story.focus') ? ` (${keys('story.focus')})` : ''}`} onClick={openFocus}>
          ⛶ Focus
        </button>
        <button
          type="button"
          disabled={problems.length > 0 || scanning}
          title="Find the lore this scene mentions, by name, alias or short name, and link it"
          onClick={() => void flush().then(() => setScanning(true))}
        >
          🔗 Scan for Connections
        </button>
        {tools}
      </div>
      {below}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}

      <div className="story-goals">
        <label>
          Scene goal
          <input
            type="number"
            min={0}
            max={STORY_LIMITS.goal}
            step={1}
            value={goal}
            placeholder="none"
            onChange={(e) => {
              setGoal(e.currentTarget.value);
              const n = e.currentTarget.value.trim() === '' ? 0 : Number(e.currentTarget.value);
              if (Number.isInteger(n) && n >= 0 && n <= STORY_LIMITS.goal) update({ wordGoal: n }, true);
            }}
          />
        </label>
        <GoalBar label="Progress toward the scene's word goal" value={words} goal={goalNumber} text={goalNumber > 0 ? `${number(words)} / ${number(goalNumber)}` : ''} />
        <GoalBar
          label="Progress toward the session goal"
          value={sessionNow}
          goal={project.sessionWordGoal}
          text={`+${number(sessionNow)} this session${project.sessionWordGoal > 0 ? ` of ${number(project.sessionWordGoal)}` : ''}`}
        />
      </div>

      <h3>Manuscript</h3>
      {problems.length > 0 ? (
        <>
          <p className="callout">
            This scene has {problems.join(', ').replace(/, ([^,]*)$/, ' and $1')}, which the web editor can't keep yet. Write its text in Realmery on the desktop, so
            nothing is lost; everything else can be changed here.
          </p>
          <div className="manuscript">
            <EntryContent html={scene.contentHtml} empty="This scene has no text yet." />
          </div>
        </>
      ) : scanning ? (
        <SceneScan
          file={file}
          sceneId={scene.id}
          html={html.current}
          references={entryIds}
          entries={entries}
          onCancel={() => setScanning(false)}
          onApply={(linked, referenceIds) => {
            // Through the editor's schema, so what's saved is what the editor writes.
            const next = htmlFromDoc(docFromHtml(linked));
            const ids = [...new Set([...entryIds, ...referenceIds])];
            html.current = next;
            counted(wordCount(next));
            setEntryIds(ids);
            setGeneration((g) => g + 1);
            update({ contentHtml: next, entryIds: ids }, true);
            setScanning(false);
          }}
        />
      ) : (
        !focus && (
          <>
            <RichTextEditor
              key={generation}
              label="Manuscript"
              mentionCandidates={entries}
              proofing={{ file, scopeId: scene.id, noun: 'Scene' }}
              initialHtml={html.current}
              onChange={(next) => {
                html.current = next;
                counted(wordCount(next));
                update({ contentHtml: next }, true);
              }}
              onOpenEntry={(entryId) => void flush().then(() => navigate({ to: '/realm/$realmKey/entry/$entryId', params: { realmKey, entryId }, search: {} }))}
            />
            <p className="meta">Type [[ to mention an entry. Ctrl+click (Cmd+click on a Mac) a mention to open it.</p>
          </>
        )
      )}
      {focus && (
        <FocusMode
          proofing={{ file, scopeId: scene.id, noun: 'Scene' }}
          title={title}
          html={html.current}
          wordGoal={goalNumber}
          idleMinutes={settings()['writing.focusIdleMinutes']}
          mentionCandidates={entries}
          onChange={(next) => {
            html.current = next;
            counted(wordCount(next));
            update({ contentHtml: next }, true);
          }}
          onDone={(sitting) => void finishSitting(sitting)}
        />
      )}

      <h3>Story beats</h3>
      <label>
        <span className="visually-hidden">Story beats</span>
        <textarea
          value={beats}
          rows={3}
          maxLength={STORY_BEATS_MAX}
          placeholder="What is this scene's purpose? What changes by the end of it?"
          onChange={(e) => {
            setBeats(e.currentTarget.value);
            update({ storyBeats: e.currentTarget.value });
          }}
        />
      </label>

      <h3>When it happens</h3>
      <label className="check">
        <input type="checkbox" checked={hasDate} onChange={(e) => changeDate(e.currentTarget.checked, date)} />
        Has a place in the Chronology
      </label>
      {hasDate && <DateFields legend="Date" value={date} onChange={(d) => changeDate(true, d)} calendar={view.calendar} />}
      {dateProblem && (
        <p className="notice-error" role="alert">
          {dateProblem}
        </p>
      )}

      <h3>Characters, locations and lore</h3>
      <Picker
        legend="Entries in this scene"
        items={entries.map((e) => ({ id: e.id, name: e.title, note: e.typeName }))}
        chosen={entryIds}
        onChange={(ids) => {
          setEntryIds(ids);
          update({ entryIds: ids });
        }}
        searchLabel="Find an entry"
        none="The realm has no entries yet."
      />
      <h3>Linked events</h3>
      <Picker
        legend="Chronology events this scene shows"
        items={view.events.map(({ event, timeline }) => ({ id: event.id, name: event.title, note: timeline.name }))}
        chosen={eventIds}
        onChange={(ids) => {
          setEventIds(ids);
          update({ eventIds: ids });
        }}
        searchLabel="Find an event"
        none="The realm has no Chronology events yet."
      />

      <Revisions
        file={file}
        view={view}
        onRestored={(restored) => {
          html.current = restored;
          counted(wordCount(restored));
          setGeneration((g) => g + 1);
        }}
        flush={flush}
      />
    </section>
  );
}

function GoalBar({ label, value, goal, text }: { label: string; value: number; goal: number; text: string }) {
  return (
    <span className="goal-bar">
      {goal > 0 && <progress max={goal} value={Math.min(value, goal)} aria-label={label} />}
      {text && (
        <span className="meta">
          {text}
          {goal > 0 && value >= goal ? ' ✓' : ''}
        </span>
      )}
    </span>
  );
}

/** The scene's earlier versions (Revision History): each to look at, and to put back. */
function Revisions({ file, view, onRestored, flush }: { file: string; view: SceneView; onRestored: (html: string) => void; flush: () => Promise<void> }) {
  const router = useRouter();
  const [shown, setShown] = useState<{ id: string; html: string } | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [error, setError] = useState('');
  const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

  async function look(id: string) {
    setError('');
    if (shown?.id === id) return setShown(null);
    try {
      const html = await realms.sceneRevision(file, id);
      if (html === null) throw new Error('That earlier version is no longer kept.');
      setShown({ id, html });
    } catch (e) {
      setError(message(e, (e as Error).message));
    }
  }

  async function restore(id: string) {
    setError('');
    try {
      await flush();
      await realms.restoreRevision(file, view.scene.id, id, settings()['writing.sceneRevisions']);
      const html = await realms.sceneRevision(file, id);
      setRestoring(null);
      setShown(null);
      if (html !== null) onRestored(html);
      await router.invalidate();
    } catch (e) {
      setError(message(e, 'That version could not be restored.'));
    }
  }

  return (
    <>
      <h3>Revision history</h3>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {view.revisions.length === 0 ? (
        <p className="meta">No earlier versions yet. One is kept each time the scene's text is saved.</p>
      ) : (
        <ol className="story-children revisions">
          {view.revisions.map((r) => (
            <li key={r.id}>
              <span>
                {when(r.created)} — {number(r.wordCount)} {r.wordCount === 1 ? 'word' : 'words'}
              </span>
              <span className="story-child-tools">
                <button type="button" className="quiet" aria-expanded={shown?.id === r.id} onClick={() => void look(r.id)}>
                  {shown?.id === r.id ? 'Hide' : 'View'}
                </button>
                <button type="button" className="quiet" onClick={() => setRestoring(r.id)}>
                  Restore…
                </button>
              </span>
              {restoring === r.id && (
                <div className="panel confirm" role="alertdialog" aria-label="Restore this version">
                  <p>Replace the scene's text with this version from {when(r.created)}? The text as it is now stays in the history.</p>
                  <div className="actions">
                    <button type="button" className="primary" onClick={() => void restore(r.id)}>
                      Restore
                    </button>
                    <button type="button" onClick={() => setRestoring(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {shown?.id === r.id && (
                <div className="manuscript revision-text">
                  <EntryContent html={shown.html} empty="This version had no text." />
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </>
  );
}

/** The project's writing log (`buildWritingLog`): each focus-mode sitting, and the last week's and all time's. */
export function WritingLog({ sittings, now = Date.now() }: { sittings: readonly WritingSession[]; now?: number }) {
  const focusKeys = keysOf('story.focus').map(keyLabel)[0];
  if (sittings.length === 0) {
    return (
      <p className="meta">
        Sittings in focus mode (⛶ Focus{focusKeys ? `, or ${focusKeys} in a scene` : ''}) are logged here: how long you wrote, and how many words it added.
      </p>
    );
  }
  const weekAgo = now - 7 * 24 * 3600_000;
  const week = sittings.filter((s) => Date.parse(s.started) > weekAgo);
  const seconds = (list: typeof sittings) => list.reduce((sum, s) => sum + s.activeSeconds, 0);
  const signed = (n: number) => `${n >= 0 ? '+' : '−'}${number(Math.abs(n))}`;
  const weekWords = week.reduce((sum, s) => sum + s.wordsEnd - s.wordsStart, 0);
  const shown = sittings.slice(0, 10);
  const when = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  return (
    <>
      <p className="meta">
        Last 7 days: {describeWritingTime(seconds(week))} over {week.length} {week.length === 1 ? 'sitting' : 'sittings'}, {signed(weekWords)} words · All time:{' '}
        {describeWritingTime(seconds(sittings))}
      </p>
      <table className="writing-log">
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col">Scene</th>
            <th scope="col">Writing</th>
            <th scope="col">Words</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((s) => {
            const added = s.wordsEnd - s.wordsStart;
            return (
              <tr key={s.id}>
                <td className="meta">{when(s.started)}</td>
                <td>{s.sceneTitle.trim() === '' ? '(untitled scene)' : s.sceneTitle}</td>
                <td>{describeWritingTime(s.activeSeconds)}</td>
                <td className={added >= 0 ? 'writing-log-gain' : 'writing-log-loss'}>{signed(added)} words</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {sittings.length > shown.length && <p className="meta">…and {sittings.length - shown.length} earlier</p>}
    </>
  );
}
